import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  clampFrontStickerPlacement,
  type BoardFaceLinkCardSurface,
  type BoardFaceList,
  type CardAgingMode,
  type CardMirror,
  type PlacedSticker,
  type StickerUpdateBody,
} from '@molt/shared';
import { useEffect, useRef, useState, type DragEvent, type ReactElement } from 'react';
import type { BoardCardOpenIntent } from '@/lib/board-card-return';
import { cn } from '@/lib/cn';
import { CARD_DRAG_TYPE } from '@/lib/card-drag';
import { isOptimisticCardId } from '@/lib/cards';
import type { DragHandle } from '@/lib/list-drag';
import type { PowerUpSurfaceSummary } from '@/lib/power-ups';
import { CardTile, type CardSelectionInput } from './CardTile';
import { QuickEdit } from './QuickEdit';

/**
 * The `<li>` of a card — chunk `C3`, agent E (`FE-12.3`, `FR-BOARD-025`).
 * Signatures fixed by `C3-BRIEF.md` §6.4. The mirror of `SortableListColumn`'s
 * rule: **the one place `useSortable` is called for a card.** It renders the
 * `<li>`; `ListColumn` (agent F) no longer does.
 *
 * `data: { type: CARD_DRAG_TYPE, listId }` is the discriminator `lib/card-
 * drag.ts`'s `isCardDrag` and `boardCollisionDetection` read (§7.6) — it is
 * what lets the board's ONE `DndContext` carry a list drag and a card drag at
 * once without either function guessing from shape alone.
 *
 * ── Why the `<li>` carries no size/layout classes ───────────────────────────
 * Unlike `SortableListColumn`'s `<li>` (which owns the 292px column width,
 * because a list is the thing being measured for the strip's own layout), a
 * card's box comes entirely from `CardTile`'s own classes — `ListColumn`'s
 * `<ol>` already supplies the vertical `gap` (`SETTLED.cardGap`, inline style)
 * and is a plain flex column, so this `<li>` needs nothing beyond what C1's
 * inline version had (nothing at all) plus the drag transform below. Adding a
 * width/height class here would double-count against `lib/density.ts`, which
 * reads `CardTile`'s classes, not this file's.
 *
 * ── Two disable reasons, same mechanism, different why ─────────────────────
 * `useSortable({ disabled })` is `true` for the SAME two reasons
 * `SortableListColumn` disables a column:
 *
 *   1. **An optimistic card** (`isOptimisticCardId`, brief §5.4) — its id is
 *      `optimistic:<uuid>` and the server has never seen it. Dragging it (or
 *      returning it as a neighbour of a real drop) sends a fake id and the
 *      404 is untraceable back to a drag.
 *   2. **A viewer who cannot edit.** Not named in §6.4's prose, added the same
 *      way `SortableListColumn` added it for lists — `canEdit` gates whether
 *      `CardTile` gets a `dragHandle` at all, and `disabled` withdraws the
 *      `<li>` as a *droppable* too, which a missing handle alone does not.
 *      Reported as an addition, same as the list precedent.
 *
 * ⚠ Plain props to `CardTile`, never conditional JSX spreads — the C2 defect
 * `CardTile.tsx`'s own header and this file's stub both name: JSX spread
 * attributes bypass excess-property checking, so a conditional spread would
 * compile even if `CardTileProps` did not declare a prop at all. `dragHandle`
 * is `| undefined` on both interfaces for exactly this reason.
 *
 * ── ⚠ `onQuickEditClose`, ADDED beyond `C3-BRIEF.md` §6.4, and reported ────
 * `QuickEdit.tsx` (agent D)'s own file header, which landed after this file's
 * signature was fixed, settles a question §6.4 leaves open: it "renders in
 * place of the tile's content, inside the same `<li>` (`SortableCardTile`'s
 * … this file never renders one of its own)". So THIS file — not
 * `ListColumn`, not `CardTile` — is where `<QuickEdit>` has to mount, and
 * `QuickEditProps.onClose: () => void` is required. Nothing in §6.4's fixed
 * `SortableCardTileProps` supplies one: `onQuickEdit` is documented as
 * OPENING only ("Opens quick edit"), and calling it again while already
 * `quickEditing` would re-set the same card, not close it. Rather than
 * invent a close path out of a prop documented for the opposite direction,
 * this adds one more OPTIONAL prop — additive, so a caller passing only the
 * brief's four still compiles — and falls back to rendering `CardTile` as
 * before when it is absent, rather than mounting `QuickEdit` with no way to
 * close. `ListColumn` (agent F) is the natural owner of the closing half —
 * it already owns `quickEditCardId`/`onQuickEditChange` (`C3-BRIEF.md`
 * §6.5) — and the obvious wiring is `onQuickEditClose={() =>
 * onQuickEditChange(null)}`. Flagged in the return for the parent to confirm
 * rather than assumed silently.
 */
/**
 * `FE-58.1` / `FR-EXP-027` AC1 / `FR-CARD-032` AC1 — the media type the
 * board-menu sticker tray writes on `dataTransfer` and this file's drop
 * target reads back. **The tray must call
 * `event.dataTransfer.setData(STICKER_DRAG_MEDIA_TYPE, sticker.id)` in its
 * `onDragStart`, or a drop here is ignored** — deliberately, because an
 * untyped native drag over a card is a file, a link or the board menu's own
 * pinned-view reorder (`BoardFace.tsx`'s `'text/plain'` drag), never a
 * sticker.
 *
 * ⚠ **The tray's `effectAllowed` is the OTHER half of the same contract, and
 * getting it wrong fires no `drop` event at all.** The HTML drag-and-drop
 * processing model forces the drag operation to `none` — silently, with no
 * `drop` — when a target's `dropEffect` is incompatible with the source's
 * `effectAllowed`, and `CardChecklistSection.tsx`, the very file the media
 * type below is modelled on, sets `effectAllowed = 'move'`. So this file
 * DERIVES `dropEffect` from whatever the tray allowed rather than asserting
 * `'copy'` (see `handleStickerDragOver`), and any `effectAllowed` but
 * `'none'` works. jsdom performs no negotiation whatsoever, so a mismatch
 * here would be a browser-only failure behind a green unit suite.
 *
 * `application/x-molt-…` is the house shape, not an invention:
 * `CardChecklistSection.tsx` already ships
 * `'application/x-molt-checklist-item'` for the same purpose one directory
 * over, and `PlannerPage.tsx` guards the same way
 * (`Array.from(event.dataTransfer.types).includes(…)`).
 *
 * ⚠ **It lives here, in the DROP target's file, and that is the ownership
 * rule rather than an accident of who wrote it first.** `lib/card-drag.ts`
 * — where `CARD_DRAG_TYPE` lives — is `AUDIT.md` §1.2's whole reason the
 * sticker drag is native rather than dnd-kit: that file is not this chunk's
 * to edit, and `boardCollisionDetection` would route a third dnd-kit drag
 * type into the LIST branch, where every card droppable has already been
 * filtered out. Nothing about the sticker drag may accrete there.
 */
export const STICKER_DRAG_MEDIA_TYPE = 'application/x-molt-sticker';

/** What a completed tray drop hands its composer, in the card's own pixels. */
export interface CardFrontStickerPlacement {
  readonly stickerId: string;
  readonly positionX: number;
  readonly positionY: number;
}

export interface SortableCardTileProps {
  card: BoardFaceList['cards'][number];
  cardAgingMode?: CardAgingMode | undefined;
  /**
   * `FE-25.2` / `FR-CARD-025` AC5 — the board's `covers_enabled` setting,
   * now real on the wire as `boardContentsBoardSchema.coversEnabled` (this
   * chunk's contract, `AUDIT.md` §4.3). Threaded exactly as `cardAgingMode`
   * is: a board-level RENDER setting that only `CardTile` can act on, so
   * this file carries it and decides nothing.
   *
   * ⚠ **No `?? true` here, and that is deliberate.** §9 puts the default in
   * ONE place — `CardTile`, the renderer — because `CardTile` must handle
   * `undefined` anyway: `ListColumn.tsx` and `BoardFace.tsx` are the two
   * composers that will pass `coversEnabled={board.coversEnabled}` beside
   * their existing `cardAgingMode={board.cardAgingMode}`, and **neither is
   * owned by this chunk**. Defaulting on both sides of one prop is drift
   * nothing can see. The optionality is what keeps those two unowned files
   * compiling until that one line each lands.
   *
   * ⚠ The server **projects the flag and filters nothing** — see
   * `board-contents.integration.test.ts`'s named ruling (B15's colourless
   * labels, one table over). Suppression is the client's act, and it
   * happens in `CardTile`, one file down from here.
   */
  coversEnabled?: boolean | undefined;
  /**
   * `FR-CARD-005`'s optional board setting (`docs/notes/card-keys.md`) —
   * `boardContentsBoardSchema.showCardNumbersOnFront`, threaded on
   * `coversEnabled`'s footing. When `true`, `CardTile` draws the card's `ref`
   * (`MG-0001`) muted before the title; a card whose `ref` is `null` (a viewer
   * outside the reference scope) draws nothing.
   */
  showCardRef?: boolean | undefined;
  /** Owner-directed: board's `dangerousComments` threshold; cards at or above it get a red tint. `null`/undefined = off. */
  dangerousComments?: number | null | undefined;
  listId: string;
  canEdit: boolean;
  /** Quick edit is open on this card. */
  quickEditing?: boolean | undefined;
  /** Opens quick edit. Undefined when the viewer cannot edit — no pencil, no handlers. */
  onQuickEdit?: (() => void) | undefined;
  /** Archives the card from its front. Undefined when the viewer cannot edit. */
  onArchive?: (() => void) | undefined;
  /** Toggles completion from the card front. Undefined when the viewer cannot edit. */
  onToggleCompleted?: (() => void) | undefined;
  /** Opens the card move dialog. Undefined until FE-21.1 is wired. */
  onMove?: (() => void) | undefined;
  /** Opens the card copy dialog. Undefined until FE-35.1 is wired. */
  onCopy?: (() => void) | undefined;
  /** Closes quick edit. See the file header — added beyond §6.4's fixed four. */
  onQuickEditClose?: (() => void) | undefined;
  /**
   * `FE-58.1` — a sticker dropped from the board-menu tray onto THIS card's
   * front, at a coordinate already clamped into the tile's own box by
   * `clampFrontStickerPlacement` (`@molt/shared`, `AUDIT.md` §4.1/§5).
   *
   * ⚠ **No `cardId`.** The composer binds the card, exactly as it binds
   * `onArchive` and `onToggleCompleted`; this file holds one card and
   * decides nothing beyond where inside it the pointer landed.
   *
   * ⚠ **The POST, the invalidation and the announcement are the composer's,
   * not this file's.** `AUDIT.md` §7.1 reuses the shipped `lib/announce.ts`
   * live region rather than adding a second, and the mutation is the tray's
   * plain `placeSticker(cardId, body)` — `lib/stickers.ts`'s
   * `usePlaceSticker` binds `cardId` at hook construction and is therefore
   * useless to a tray that may drop on any of 24 cards. Neither belongs in
   * a `<li>`.
   *
   * Undefined when the viewer cannot edit — same idiom as every other
   * callback above, and this file re-gates it anyway (see `placeSticker`).
   */
  onPlaceSticker?: ((placement: CardFrontStickerPlacement) => void) | undefined;
  /**
   * `FR-EXP-027` AC2 — the hovered card-front sticker the member asked to
   * remove. Pure pass-through to `CardTile`, which owns the affordance.
   *
   * ⚠ **It REQUESTS; it must not delete.** AC2 is *"hovers it and selects
   * remove **and confirms**"*, and `FR-CARD-032` says sticker deletion has no
   * undo. The confirmation is the composer's — the same dialog, under the same
   * `cardBack.stickers.remove.*` keys, the card back already ships.
   *
   * ⚠ **No `cardId`, for the same reason `onPlaceSticker` carries none**: the
   * composer binds the card. `PlacedSticker.id` is the placement id the
   * `DELETE` takes, but the composer still needs the CARD to invalidate
   * `queryKeys.cards.detail`, and this file holds exactly one card.
   *
   * Gated on `canAct` below — the gate `onArchive` and `onPlaceSticker` use,
   * NOT the narrower `canEditOrdinaryFields`: a link card already DISPLAYS
   * placed stickers, so a surface that shows one it cannot remove is the
   * asymmetry `placeSticker`'s own docstring rejects.
   */
  onRequestRemoveSticker?: ((sticker: PlacedSticker) => void) | undefined;
  /** `FR-CARD-032`: request card-front rotation or stacking-order adjustment. */
  onAdjustSticker?:
    ((sticker: PlacedSticker, body: StickerUpdateBody) => void) | undefined;
  showLabelNames?: boolean | undefined;
  onOpenCard?: ((intent: BoardCardOpenIntent) => void) | undefined;
  onOpenDates?: ((intent: BoardCardOpenIntent) => void) | undefined;
  highlighted?: boolean | undefined;
  listTitle?: string | undefined;
  cardIndex?: number | undefined;
  listCardCount?: number | undefined;
  selected?: boolean | undefined;
  selectionActive?: boolean | undefined;
  onSelectGesture?:
    | ((input: CardSelectionInput, card: BoardFaceList['cards'][number]) => void)
    | undefined;
  onOpenBulkActions?: (() => void) | undefined;
  onRemoveMirror?: ((mirror: CardMirror) => void) | undefined;
  onConvertLinkCard?: ((card: BoardFaceLinkCardSurface) => void) | undefined;
  pendingSync?: boolean | undefined;
  /** B65: optional template-card state, supplied by the board template list. */
  templateState?:
    | {
        readonly isTemplate: boolean;
        readonly hiddenFromList: boolean;
        readonly busy?: boolean | undefined;
      }
    | undefined;
  /** B65: hide a visible template card from everyday board flow. */
  onHideTemplateCard?: ((card: BoardFaceList['cards'][number]) => void) | undefined;
  /** B65: return a hidden template card to its list when a caller renders it. */
  onUnhideTemplateCard?: ((card: BoardFaceList['cards'][number]) => void) | undefined;
  powerUpSurfaces?: PowerUpSurfaceSummary | undefined;
}

export function SortableCardTile({
  card,
  cardAgingMode,
  coversEnabled,
  showCardRef,
  dangerousComments,
  listId,
  canEdit,
  quickEditing,
  onQuickEdit,
  onArchive,
  onToggleCompleted,
  onMove,
  onCopy,
  onQuickEditClose,
  onPlaceSticker,
  onRequestRemoveSticker,
  onAdjustSticker,
  showLabelNames,
  onOpenCard,
  onOpenDates,
  highlighted,
  listTitle,
  cardIndex,
  listCardCount,
  selected,
  selectionActive,
  onSelectGesture,
  onOpenBulkActions,
  onRemoveMirror,
  onConvertLinkCard,
  pendingSync,
  templateState,
  onHideTemplateCard,
  onUnhideTemplateCard,
  powerUpSurfaces,
}: SortableCardTileProps): ReactElement {
  const isPlaceholder = isOptimisticCardId(card.id);
  const surfaceType = 'surfaceType' in card ? card.surfaceType : 'card';
  const isMirrorSurface = surfaceType === 'mirror';
  const isLinkCardSurface = surfaceType === 'link_card';
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: card.id,
    data: { type: CARD_DRAG_TYPE, listId },
    disabled: isPlaceholder || !canEdit || isMirrorSurface,
    /*
     * ⚠ Overrides dnd-kit's own `role: 'button'` default back to `'link'`,
     * found necessary by a stronger review after the activator was moved
     * onto the card's own anchor (see `CardTile.tsx`'s header). Without
     * this, `attributes.role` — spread directly onto that anchor — would
     * flip every editable card's exposed accessible role from "link" to
     * "button", which is a product-wide regression from C1's own DOM
     * contract that nothing in this chunk's own tests would have caught
     * (every test here queries a card by NAME, and both roles carry it).
     * `useDraggable`'s `event.target !== active.activatorNode.current`
     * check (`KeyboardSensor.activators`, `@dnd-kit/core`) is pure
     * reference identity, so this is cosmetic to activation: Space still
     * picks the card up, `aria-roledescription="sortable"` still
     * announces it as one, only the base role differs. `roleDescription`
     * is left at `useSortable`'s own default (`'sortable'`) — only `role`
     * needed correcting.
     */
    attributes: { role: 'link' },
  });

  const canAct = canEdit && !isPlaceholder && !isMirrorSurface;
  const canEditOrdinaryFields = canAct && !isLinkCardSurface;

  const dragHandle: DragHandle | undefined = canAct
    ? { setActivatorNodeRef, attributes, listeners, isDragging }
    : undefined;

  // §5.4's fourth gate is `CardTile`'s own (`isOptimisticCardId` again, on
  // `card.id`) — not repeated here — but a viewer who cannot edit gets no
  // opener either, the same as the drag handle above.
  const quickEdit = canEditOrdinaryFields ? onQuickEdit : undefined;
  const archive = canAct ? onArchive : undefined;
  const toggleCompleted = canEditOrdinaryFields ? onToggleCompleted : undefined;

  // See the file header's "`onQuickEditClose`" note. `QuickEdit` needs a way
  // to close itself; only mount it once this file has been given one, so an
  // as-yet-unwired caller degrades to the ordinary tile rather than a panel
  // with no way out.
  const showQuickEdit = quickEditing === true && onQuickEditClose !== undefined;

  /*
   * ⚠ **Focus restoration on close, added by the parent at integration
   * (`C3-BRIEF.md` §12.4).** Agent D found the gap from inside `QuickEdit` and
   * could not close it: when the panel unmounts, the focused title input goes
   * with it and **focus drops to `<body>`** — a keyboard member loses their
   * place in the board entirely, having done nothing worse than press Escape.
   * `InlineEdit`'s own `returnFocus` cannot help, because its idle trigger
   * never renders inside `QuickEdit` at all.
   *
   * This file is the only one that holds the tile across BOTH states, which is
   * what makes it the owner. The focus target is the first focusable in the
   * `<li>`, found structurally (`[tabindex], a[href], button`) rather than
   * named, which is why this note only needs correcting rather than rewriting
   * below: `CardTile.tsx` was restructured, after this comment was first
   * written, to put `dragHandle`'s `tabIndex`/attributes on the stretched
   * link rather than the tile's outer `<div>` (a real defect that
   * restructure fixed — see that file's header, "EVERYTHING INTERACTIVE
   * LIVES ON THIS ELEMENT"). The query still resolves correctly with no
   * change here: the link is still the first (and, when draggable, the
   * ONLY) focusable descendant, so it is still what gets focus back, and it
   * is still the one element a keyboard drag can start from — restoring
   * focus to it restores the drag affordance too, not just a position in
   * the tab order.
   *
   * Guarded on a previous-value ref rather than run on every render: focusing
   * unconditionally would steal focus from wherever a member had moved it.
   */
  const liRef = useRef<HTMLLIElement | null>(null);
  const wasQuickEditing = useRef(showQuickEdit);
  useEffect(() => {
    const closedJustNow = wasQuickEditing.current && !showQuickEdit;
    wasQuickEditing.current = showQuickEdit;
    if (!closedJustNow) return;
    // The panel is gone and focus is on <body>; put it back on the card.
    liRef.current?.querySelector<HTMLElement>('[tabindex], a[href], button')?.focus();
  }, [showQuickEdit]);

  /*
   * ─────────────────────────────────────────────────────────────────────────
   * `FE-58.1` — THE CARD-FRONT STICKER DROP TARGET, AND WHY IT IS ON THE
   * `<li>` RATHER THAN ON THE TILE
   * ─────────────────────────────────────────────────────────────────────────
   * This is the board face's THIRD drag, and `AUDIT.md` §7 requires the
   * separation from the other two be structural rather than a timing
   * accident:
   *
   *   1. a CARD between lists — `@dnd-kit/core` 6.x (the CLASSIC tree),
   *      `PointerSensor`/`KeyboardSensor` on the activator this file wires
   *      above;
   *   2. a LIST along the strip — the same `DndContext`, `useSortable` in
   *      `SortableListColumn`;
   *   3. a STICKER, tray → card front — **native HTML5 drag-and-drop**,
   *      below.
   *
   * dnd-kit registers no `dragstart`/`dragover`/`drop` listener at all, and a
   * native drag fires no pointer events once it is under way, so neither
   * protocol can observe the other's gesture. `getMeasurableNode`, the
   * transform-aware overlay measurement and the recorded `rotate-2`
   * keyboard-drag defect **cannot reach this drag**: no `DragOverlay`
   * participates in it — the browser paints the native drag image — and this
   * file adds no transform to anything.
   *
   * ⚠ **On the `<li>`, not on `CardTile`'s root, and it is not a preference.**
   * `CardTile` renders in TWO places: inside this `<li>`, and inside
   * `BoardFace`'s `DragOverlay`. Handlers on its root would mount a second,
   * pointer-following drop target with a transformed, moving box — a
   * coordinate measured against a card that is not in any list. This `<li>`
   * is rendered in the list and nowhere else, so the drop target exists
   * exactly where a card actually sits.
   *
   * ⚠ **`event.clientX − rect.left`, never `event.offsetX`.** `offset*` is
   * relative to the EVENT TARGET, which on a card front is whichever of the
   * stretched `z-[2]` anchor, a `LabelChip`, the badge strip or an existing
   * sticker `<img>` happens to be topmost under the pointer. It produces
   * plausible-looking wrong numbers, which is the worst kind.
   *
   * ⚠ **The rect is the TILE's, resolved through `[data-card-id]`** — the
   * attribute `CardTile` already sets — and not the `<li>`'s, even though the
   * two boxes coincide today (this `<li>` carries no size, padding or margin
   * class; see the file header). `sticker.positionX/positionY` are rendered
   * by `CardTile` as `left`/`top` inside ITS box, so the box the coordinate
   * is measured against must be the box it is rendered in, or the front and
   * the card back disagree about a placement — the drift `FR-CARD-032` AC4
   * exists to forbid. It also makes the quick-edit branch fall out for free:
   * `QuickEdit` renders no `[data-card-id]`, so a sticker dropped on an open
   * quick-edit panel resolves no tile and places nothing, rather than
   * placing at panel coordinates.
   *
   * ⚠ **`event.defaultPrevented` short-circuits the drop.** A native drop
   * bubbles, so a nested handler that has already taken this drop (a future
   * per-sticker target inside `CardTile`, say) has necessarily called
   * `preventDefault` — the inner one wins and this one no-ops, rather than
   * both firing and placing the sticker twice.
   *
   * Re-gated rather than trusted from the composer, the same way
   * `onQuickEdit` and `onToggleCompleted` are two dozen lines up — and on
   * `canAct`, the gate `onArchive` uses, NOT the narrower
   * `canEditOrdinaryFields`. An optimistic card has no server id for a
   * placement to point at, and a mirror is a read-only projection of a card
   * that lives on another board; both are excluded. **A link card is not**,
   * and that is measured rather than assumed: `CardTile.tsx:849` computes
   * `frontStickers` from `card.stickers` unconditionally, so a link card
   * already DISPLAYS placed stickers. Gating placement on
   * `canEditOrdinaryFields` would leave one surface that shows stickers and
   * silently refuses to receive one — an asymmetry a member experiences as a
   * dead drop with no explanation, and no requirement asks for it.
   */
  const placeSticker = canAct ? onPlaceSticker : undefined;
  /** Same gate, same reasoning — see `onRequestRemoveSticker`'s docstring. */
  const requestRemoveSticker = canAct ? onRequestRemoveSticker : undefined;
  /** Same gate, same reasoning — see `onRequestRemoveSticker`'s docstring. */
  const requestAdjustSticker = canAct ? onAdjustSticker : undefined;

  /**
   * `FE-58.1` / `FR-CARD-032` AC1 / `FR-EXP-027` AC1 — **the accept ring's
   * state, owned by the drop target that actually accepts the drop.**
   *
   * ─────────────────────────────────────────────────────────────────────────
   * ⚠ WHY IT IS A BOOLEAN FORWARDED INTO `CardTile` AND NOT A CLASS ON THIS
   * `<li>` — the `fe-plan.md` residual's open question, settled
   * ─────────────────────────────────────────────────────────────────────────
   * The residual said the choice *"has an argument written on both sides in
   * `AUDIT.md` §7"*. It does not:
   * `docs/design/fe12-card-front-sticker-ring/AUDIT.md` §2.2 scanned all
   * 58,744 bytes of `fe9-card-front-chrome/AUDIT.md` for `outline-accent`,
   * `data-sticker-drop-active`, `stickerDropActive` and `onPlaceSticker` and
   * found **0**. The argument was constructed rather than retrieved, and
   * `CardTileProps.stickerDropActive`'s docstring carries it in full. Its
   * three measured reasons, restated from the side that consumes them:
   *
   *   1. `outline` follows the **border-radius of the element it is drawn
   *      on**. `CardSurface` is `rounded-card`; this `<li>` carries no radius
   *      (see the file header — it carries no box classes at all), so a ring
   *      here is a square-cornered rectangle around a rounded card, drawn
   *      outside the box that `highlighted` and `selected` draw
   *      `-outline-offset-2` INSIDE.
   *   2. `CardTile` already holds `highlighted`, `selected` and
   *      `stickerDropActive` as three lines of one `cn()` call. A class here
   *      would be a fourth copy of that visual, in a second file, that the
   *      first restyle would miss.
   *   3. This `<li>` is `opacity-0` while dnd-kit drags the card, so a ring
   *      painted here is one `isDragging` away from invisibility for reasons
   *      that have nothing to do with stickers.
   *
   * ⚠ **This is NOT `onPlaceSticker` passed through, and the reason that prop
   * is still withheld stands unchanged** (see the `<CardTile>` call below).
   * A boolean registers no `dragover`, no `drop` and no `preventDefault`, so
   * the two-nested-targets hazard — the inner one swallowing the `stickerId`
   * the outer reads — cannot arise from it. The state is computed HERE,
   * where the accepting target lives; it is only *painted* there.
   */
  const [stickerDropActive, setStickerDropActive] = useState(false);

  const carriesSticker = (event: DragEvent<HTMLLIElement>): boolean =>
    Array.from(event.dataTransfer.types).includes(STICKER_DRAG_MEDIA_TYPE);

  const handleStickerDragOver = (event: DragEvent<HTMLLIElement>): void => {
    if (placeSticker === undefined) return;
    if (!carriesSticker(event)) return;

    /*
     * ⚠ **The ring is set HERE — on the two IDENTITY guards above — and not
     * below, beside `preventDefault()`. That ordering is deliberate and it is
     * the one judgement call in this file.**
     *
     * The two questions are different. The guards above ask *is this our
     * drag, over a card that can receive a sticker* — which is exactly what
     * the ring answers: **which card will receive it.** `effectAllowed`,
     * below, asks a transport question — *which operation did the source
     * permit* — and nothing about it changes which card is under the pointer.
     * Nothing below is skipped or softened: under `'none'` this handler still
     * assigns no `dropEffect`, still does not `preventDefault`, and the drag
     * is still refused. Only the ring is decided earlier.
     *
     * **Measured, and this is why it matters rather than being a nicety.**
     * `BoardStickerTray.tsx:307` sets `effectAllowed = 'copy'` on every
     * sticker drag, and the tray is the only writer of
     * `STICKER_DRAG_MEDIA_TYPE` anywhere in the repository — so `'none'`
     * *while carrying that media type* is unreachable from the product and
     * reachable only from a synthetic `DataTransfer`. Chromium pins a
     * synthetic one's `effectAllowed` at `'none'` **permanently**
     * (`fe9-card-front-chrome.capture.ts` FINDING 2 measured the assignment
     * as a silent no-op), and every drag in every browser spec here is driven
     * that way today: `page.dragAndDrop` and `setInterceptDrags` appear **0
     * times** across `apps/web/e2e`. ⚠ **That is a count of what this
     * repository does, not of what Playwright can do** — Chromium honours a
     * real `effectAllowed` through `Input.dispatchDragEvent`, which is the
     * path `page.dragAndDrop` takes, so a real-drag harness is available and
     * merely has no precedent here. The claim is therefore the narrow one:
     * ordering the ring after the negotiation would put it behind a branch
     * **no harness this repository currently uses** can enter.
     *
     * ⚠ **The sufficient reason is the first one, not the harness count.**
     * Both orderings are identical in production, because the unreachable
     * branch is the only place they differ — so where they differ only in
     * observability, the observable one wins.
     */
    if (!stickerDropActive) setStickerDropActive(true);

    /*
     * ⚠ **DERIVED from the tray's `effectAllowed`, never asserted.** A
     * `dropEffect` incompatible with the source's `effectAllowed` makes the
     * user agent force the drag operation to `none` and fire **no `drop`
     * event at all** — so a hard-coded `'copy'` against a tray that set
     * `effectAllowed = 'move'` (which is what `CardChecklistSection.tsx`,
     * the nearest precedent a tray author would copy, does) is a drop target
     * that never fires, in a browser, behind a green unit suite: jsdom
     * negotiates nothing. Deriving it also keeps the failure testable, which
     * omitting the assignment altogether would not.
     */
    const allowed = event.dataTransfer.effectAllowed;
    if (allowed === 'none') return;
    event.dataTransfer.dropEffect =
      allowed === 'move' || allowed === 'linkMove'
        ? 'move'
        : allowed === 'link'
          ? 'link'
          : 'copy';

    // Without this the browser refuses the drop outright — `dragover`'s
    // default action IS "reject", which is why every native drop target in
    // this repository calls it (`CardChecklistSection`, `BoardTimelineView`).
    event.preventDefault();
  };

  /**
   * ⚠ **`dragleave` fires on this `<li>` when the pointer crosses into a
   * CHILD of it** — and this element is full of them: the tile, the stretched
   * link, the label chips, the badge strip, an already-placed sticker.
   * Clearing unconditionally would strobe the ring on every internal
   * crossing, which reads as a flicker rather than as a target. The
   * `relatedTarget` — the element being ENTERED — is tested against this
   * subtree instead. `instanceof Node` is the narrowing React's
   * `EventTarget | null` needs before `contains` will take it.
   */
  const handleStickerDragLeave = (event: DragEvent<HTMLLIElement>): void => {
    if (!stickerDropActive) return;
    const entering = event.relatedTarget;
    if (entering instanceof Node && event.currentTarget.contains(entering)) return;
    setStickerDropActive(false);
  };

  const handleStickerDrop = (event: DragEvent<HTMLLIElement>): void => {
    // The gesture is over however it ends — taken here, taken by a nested
    // target that already called `preventDefault`, or not a sticker at all.
    // Clearing first is the one path that cannot leave the ring stuck on.
    setStickerDropActive(false);
    if (placeSticker === undefined) return;
    if (event.defaultPrevented) return;
    if (!carriesSticker(event)) return;

    const stickerId = event.dataTransfer.getData(STICKER_DRAG_MEDIA_TYPE);
    if (stickerId.length === 0) return;

    const tile = liRef.current?.querySelector<HTMLElement>('[data-card-id]') ?? null;
    if (tile === null) return;

    event.preventDefault();

    const rect = tile.getBoundingClientRect();
    /*
     * The clamp is an INPUT-side act and never an output-side transform
     * (`AUDIT.md` §5.5): it runs once, here, before the composer's POST, so
     * the number that reaches the wire is already legal — inside the tile's
     * `overflow-hidden` box, floored at 0, and rounded to the four decimals
     * `numeric(8,4)`/`stickerPositionSchema` demand. `clientX − rect.left` is
     * fractional under device-pixel-ratio and browser zoom, so an unrounded
     * coordinate is a 422 on an ordinary drag. Every reader still renders
     * `sticker.positionX` verbatim.
     */
    const { positionX, positionY } = clampFrontStickerPlacement({
      positionX: event.clientX - rect.left,
      positionY: event.clientY - rect.top,
      tileWidth: rect.width,
      tileHeight: rect.height,
    });

    placeSticker({ stickerId, positionX, positionY });
  };

  return (
    <li
      ref={(node) => {
        liRef.current = node;
        setNodeRef(node);
      }}
      style={{
        // `CSS.Translate`, not `CSS.Transform` — `SortableListColumn`'s own
        // reasoning applies unchanged: a scale can only ever smear text here
        // too, and translation is the whole of what a reorder needs.
        transform: CSS.Translate.toString(transform),
        transition,
      }}
      /*
       * ⚠ `opacity-0` while dragging, added by the parent at integration on
       * agent G's finding, and it is not cosmetic — it is what makes
       * `FR-BOARD-025`'s placeholder gap exist at all.
       *
       * With a `DragOverlay` mounted, `@dnd-kit/sortable` sets
       * `useDragOverlay = true`, so `shouldDisplaceDragSource` is `false` —
       * yet `verticalListSortingStrategy` still returns a transform for the
       * active index. The card is therefore drawn TWICE: once under the
       * pointer in the overlay, and once fully opaque in the slot it would
       * land in. Hiding the second copy turns it into the empty gap the
       * requirement's behaviour sentence asks for.
       *
       * Read out of `sortable.esm.js`, not inferred — and invisible to every
       * unit test in this chunk, because jsdom has no rects and `DragOverlay`
       * renders `null` without a live drag.
       */
      className={cn(isDragging && 'relative z-10 opacity-0')}
      /*
       * Plain props, never a conditional JSX spread — the C2 defect this
       * file's header names. Both handlers no-op when `placeSticker` is
       * undefined, so attaching them unconditionally costs nothing and
       * keeps one code path rather than two.
       */
      onDragOver={handleStickerDragOver}
      onDragLeave={handleStickerDragLeave}
      onDrop={handleStickerDrop}
    >
      {showQuickEdit ? (
        <QuickEdit
          boardId={card.boardId}
          listId={listId}
          card={card}
          onClose={onQuickEditClose}
          onMove={onMove}
          onCopy={onCopy}
        />
      ) : (
        <CardTile
          card={card}
          listId={listId}
          cardAgingMode={cardAgingMode}
          /*
           * Plain prop, never a conditional JSX spread — the C2 defect this
           * file's header names: a spread bypasses excess-property checking,
           * so `{...(coversEnabled === undefined ? {} : { coversEnabled })}`
           * would compile even if `CardTileProps` never declared the prop at
           * all, and the suppression would silently never happen.
           *
           * `stickers` and `watching` — the other two fields of this chunk's
           * contract — need no line here at all: they ride on the card
           * itself (`boardFaceCardSchema`), so `card={card}` above already
           * delivers both to `CardTile`/`CardBadges`. Correct by
           * construction, not by omission.
           */
          coversEnabled={coversEnabled}
          showCardRef={showCardRef}
          dangerousComments={dangerousComments}
          /*
           * ⚠ Always `false`, deliberately, NOT the raw `quickEditing` prop.
           * This is the `!showQuickEdit` branch, reached either because
           * quick edit is genuinely closed OR because it is `true` but no
           * `onQuickEditClose` has been wired yet (the degraded case the
           * file header names). Passing the raw prop through in that second
           * case would make `CardTile` withhold all three openers (§6.4) for
           * a card with no panel anywhere to show — inert, with no way to
           * ever open quick edit again.
           */
          quickEditing={false}
          onQuickEdit={quickEdit}
          onArchive={archive}
          /*
           * `FR-CARD-032` AC1 / `FR-EXP-027` AC1 — **the accept ring, made
           * reachable.** `stickerDropActive`'s docstring above carries the
           * ruling; this is the whole of the wiring. A plain prop, never a
           * conditional JSX spread: a spread bypasses excess-property
           * checking, so `{...(stickerDropActive ? { stickerDropActive } : {})}`
           * would compile even if `CardTileProps` never declared the field
           * and the ring would silently never appear — the exact failure
           * shape the residual described.
           *
           * ⚠ **`onPlaceSticker` is STILL not forwarded, and the boolean does
           * not weaken that.** This file's own `<li>` is the drop target;
           * wiring `CardTile`'s inner one as well would make the inner
           * handler `preventDefault` and swallow the `stickerId` the outer
           * target reads off `dataTransfer` (see `handleStickerDrop`). A
           * boolean carries no handler, no coordinate and no sticker id, so
           * it adds no second target — it only tells the tile what this
           * target already knows.
           *
           * ⚠ **No transform, in either file.** The ring is an `outline`,
           * which paints outside layout and outside the transform stack;
           * `BoardFace`'s `DragOverlay` renders `CardTile` as its single
           * measured child and `getMeasurableNode` measures exactly that
           * child, which is why the recorded `rotate-2` defect made a card
           * undraggable by keyboard. Nothing here moves a box.
           */
          stickerDropActive={stickerDropActive}
          /*
           * `FR-EXP-027` AC2. A plain prop, never a conditional spread — the
           * same C2 defect this file's header names.
           */
          onRequestRemoveSticker={requestRemoveSticker}
          onAdjustSticker={requestAdjustSticker}
          onToggleCompleted={toggleCompleted}
          dragHandle={dragHandle}
          showLabelNames={showLabelNames}
          onOpenCard={onOpenCard}
          onOpenDates={canEditOrdinaryFields ? onOpenDates : undefined}
          highlighted={highlighted}
          listTitle={listTitle}
          cardIndex={cardIndex}
          listCardCount={listCardCount}
          selected={selected}
          selectionActive={selectionActive}
          onSelectGesture={onSelectGesture}
          onOpenBulkActions={onOpenBulkActions}
          onRemoveMirror={onRemoveMirror}
          onConvertLinkCard={onConvertLinkCard}
          pendingSync={pendingSync}
          templateState={templateState}
          onHideTemplateCard={onHideTemplateCard}
          onUnhideTemplateCard={onUnhideTemplateCard}
          powerUpSurfaces={powerUpSurfaces}
        />
      )}
    </li>
  );
}
